import {
  Box,
  Button,
  Icon,
  Image,
  Theme,
  Typography,
  TypographyVariant,
  styled,
  useTheme,
} from "@outschool/backpack";
import { faLocationDot } from "@outschool/icons";
import { onlineClassesPath } from "@outschool/routes";
import { TrustpilotMicroCombo } from "@outschool/ui-components-website";
import { useIsMobile } from "@outschool/ui-utils";
import { TFunction } from "i18next";
import React from "react";
import { NavLink } from "react-router-dom";

import CodingImage from "../../../../../images/class-topics/Illustration_Coding_200x174.png";
import FullCurriculumImage from "../../../../../images/class-topics/Illustration_Full_Curriculum_200x174.png";
import LanguagesImage from "../../../../../images/class-topics/Illustration_Languages_200x174.png";
import MathImage from "../../../../../images/class-topics/Illustration_Math_200x174.png";
import MusicImage from "../../../../../images/class-topics/Illustration_Music_200x174.png";
import ReadingImage from "../../../../../images/class-topics/Illustration_Reading_200x174.png";
import SocialEmotionalImage from "../../../../../images/class-topics/Illustration_Social_Emotional_200x174.png";
import WritingImage from "../../../../../images/class-topics/Illustration_Writing_200x174.png";
import SchoolTeacherJpg from "../../../../../images/school-page/teacher.jpg";
import * as Routes from "../../../../../shared/Routes";
import Link from "../../../Link";
import { Topic, TopicsShowcase } from "../../../TopicsShowcase";
import {
  DesktopCategoryHero,
  DesktopHeroImage,
  DesktopHeroTitle,
  MobileCategoryHero,
  MobileHeroTitle,
} from "../../HeroComponents";

export const StyledNavLink = styled(NavLink)(({ theme }) => ({
  textDecoration: "none !important",
  color: `${theme.palette.grey[800]} !important`,
}));

type ImageCardParams = {
  text: string;
  imgUrl: string;
  cardWidth?: number;
  imgAspectRatioHeight?: number;
  imgAspectRatioWidth?: number;
  hideBorder?: boolean;
};

export const ImageCard = ({
  text,
  imgUrl,
  imgAspectRatioHeight,
  imgAspectRatioWidth,
  cardWidth,
  hideBorder = false,
}: ImageCardParams) => {
  return (
    <Box
      flex
      sx={(theme: Theme) => ({
        flexDirection: "column",
        alignItems: "center",
        borderRadius: 16,
        border: hideBorder ? "none" : `1px solid ${theme.colors.gray200}`,
        width: cardWidth || "auto",
        [theme.breakpoints.down("md")]: {
          maxWidth: "100%",
        },
      })}
    >
      <Box
        sx={{
          overflow: "hidden",
          aspectRatio: `${imgAspectRatioWidth}/${imgAspectRatioHeight}`,
        }}
      >
        <Image
          sx={{
            objectFit: "cover",
            width: "100%",
            height: "100%",
            borderRadius: "16px 16px 0px 0px",
          }}
          src={imgUrl}
        />
      </Box>
      <Box
        sx={(theme: Theme) => ({
          textAlign: "center",
          alignContent: "start",
          marginTop: "1em",
          marginBottom: "1em",
          [theme.breakpoints.down("md")]: {
            minHeight: 55,
          },
        })}
      >
        <Typography variant="h5">{text}</Typography>
      </Box>
    </Box>
  );
};

type HeroLocationTextParams = {
  address?: string;
  city?: string;
  state?: string;
};

const HeroLocationText = ({ address, city, state }: HeroLocationTextParams) => {
  const theme = useTheme();
  if (!address || !city || !state) {
    return null;
  }
  return (
    <Box
      flex
      sx={{
        alignItems: "center",
        gap: ".3em",
        marginBottom: "1em",
        fontWeight: "normal",
        color: theme.colors.gray600,
      }}
    >
      <Icon className="fas" icon={faLocationDot} />
      <Typography variant="subtitle2">{`${address}, ${city}, ${state}`}</Typography>
    </Box>
  );
};

type HeroCTAParams = {
  buttonText: string;
};

const HeroCTA = ({ buttonText }: HeroCTAParams) => (
  <Button
    variant="contained"
    component={Link}
    sx={{ maxWidth: 260 }}
    to={{
      pathname: Routes.onboardingQuizPath(),
    }}
  >
    {buttonText}
  </Button>
);

type SchoolPageHeroParams = {
  address: string;
  city: string;
  state: string;
  name: string;
  tStub: TFunction;
};

export const SchoolPageHero = ({
  address,
  city,
  state,
  name,
  tStub,
}: SchoolPageHeroParams) => {
  const isMobile = useIsMobile();
  const mainHeroText = tStub(
    "Outschool empowers students from schools like {{name}} to excel academically and enhance their educational experience beyond the classroom.",
    { name },
  );
  const heroTitleText = tStub("Online learning for {{name}}", { name: name });
  const heroCTAButtonText = tStub("Get a head start now!");
  const heroImageAlt = tStub(
    "Teachers to help your learner succeed are on Outschool",
  );
  return isMobile ? (
    <MobileCategoryHero
      HeroContent={
        <Box
          flex
          sx={{ padding: "1em", paddingBottom: "3em", flexDirection: "column" }}
        >
          <Box flex sx={{ flexDirection: "column", marginBottom: "3em" }}>
            <MobileHeroTitle title={heroTitleText} />
            <HeroLocationText address={address} city={city} state={state} />
            <Typography>{mainHeroText}</Typography>
          </Box>
          <HeroCTA buttonText={heroCTAButtonText} />
        </Box>
      }
    />
  ) : (
    <DesktopCategoryHero
      HeroTitle={<DesktopHeroTitle title={heroTitleText} />}
      HeroImage={
        <DesktopHeroImage imageUrl={SchoolTeacherJpg} alt={heroImageAlt} />
      }
      HeroContent={
        <>
          <HeroLocationText address={address} city={city} state={state} />
          <Typography>{mainHeroText}</Typography>
          <Box
            flex
            sx={{ flexDirection: "column", gap: "3em", marginTop: "3em" }}
          >
            <HeroCTA buttonText={heroCTAButtonText} />
            <TrustpilotMicroCombo />
          </Box>
        </>
      }
    />
  );
};

type PopularClassTopicsProps = {
  tStub: TFunction;
  titleVariant?: TypographyVariant;
};

export const PopularClassTopics = ({
  tStub,
  titleVariant,
}: PopularClassTopicsProps) => {
  const title = tStub("Popular class topics");
  const classTopics: Topic[] = [
    {
      image: FullCurriculumImage,
      text: tStub("Full curriculum"),
      path: onlineClassesPath("full-curriculum"),
    },
    {
      image: WritingImage,
      text: tStub("Writing"),
      path: onlineClassesPath("writing"),
    },
    {
      image: LanguagesImage,
      text: tStub("Languages"),
      path: onlineClassesPath("languages"),
    },
    {
      image: MusicImage,
      text: tStub("Music"),
      path: onlineClassesPath("music"),
    },
    {
      image: ReadingImage,
      text: tStub("Reading"),
      path: onlineClassesPath("reading"),
    },
    {
      image: MathImage,
      text: tStub("Math"),
      path: onlineClassesPath("math"),
    },
    {
      image: CodingImage,
      text: tStub("Coding"),
      path: onlineClassesPath("coding"),
    },
    {
      image: SocialEmotionalImage,
      text: tStub("Social-emotional"),
      path: onlineClassesPath("social-emotional"),
    },
  ];
  const sectionTrackingName = "popular_class_topics";
  const buttonTrackingName = "popular_class_topic";
  const imageTrackingName = "popular-class-topic-image";

  return TopicsShowcase({
    title,
    topics: classTopics,
    sectionTrackingName,
    buttonTrackingName,
    imageTrackingName,
    titleVariant,
  });
};
